import { StandardEvents } from '@shopify/events';

class StickerArtwork extends HTMLElement {
  #controller;
  #url;
  #busy = false;
  #serial = 0;

  connectedCallback() {
    if (this.#controller) return;
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.querySelector('[data-artwork-file]')?.addEventListener('change', () => this.loadArtwork(), options);
    this.querySelector('[data-artwork-layout]')?.addEventListener('change', () => {
      this.applyLayout();
      this.querySelector('[data-artwork-confirm]').checked = false;
    }, options);
    this.closest('form')?.addEventListener('submit', (event) => {
      if (this.#busy || !this.validateArtwork()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { ...options, capture: true });
    this.closest('.shopify-section, dialog, product-card')?.addEventListener(StandardEvents.productSelect, (event) => {
      const picker = event.target.closest?.('variant-picker');
      if (!picker || picker.dataset.productId !== this.dataset.productId) return;
      const selection = picker.getAllSelectedOptions().find((option) => option.name === 'Size')?.value;
      if (selection) this.updateShape(selection);
      this.querySelector('[data-artwork-confirm]').checked = false;
      event.promise?.then(({ detail }) => {
        if (detail?.resource?.option1) this.updateShape(detail.resource.option1);
      }).catch(() => {});
    }, options);
    this.updateShape(this.querySelector('[data-artwork-size]').textContent.trim());
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = undefined;
    if (this.#url) URL.revokeObjectURL(this.#url);
    this.#url = undefined;
  }

  set uploading(value) {
    this.#busy = value;
    this.setAttribute('aria-busy', String(value));
    this.querySelector('[data-artwork-status]').textContent = value ? this.dataset.uploading : '';
  }

  validateArtwork() {
    const input = this.querySelector('[data-artwork-file]');
    const file = input.files?.[0];
    let error = '';
    if (!file) error = this.dataset.fileRequired;
    else if (file.size > 10 * 1024 * 1024) error = this.dataset.fileLarge;
    else if (!/\.(png|jpe?g|webp|svg|pdf|eps|tiff?)$/i.test(file.name)) error = this.dataset.fileType;
    input.setCustomValidity(error);
    if (error) {
      this.querySelector('[data-artwork-status]').textContent = error;
      input.reportValidity();
      return false;
    }
    return this.closest('form').reportValidity();
  }

  failUpload() {
    this.uploading = false;
    this.querySelector('[data-artwork-status]').textContent = this.dataset.fileError;
  }

  async loadArtwork() {
    const serial = ++this.#serial;
    const input = this.querySelector('[data-artwork-file]');
    const file = input.files?.[0];
    const image = this.querySelector('[data-artwork-image]');
    image.hidden = true;
    image.removeAttribute('src');
    this.querySelector('[data-artwork-placeholder]').hidden = false;
    this.querySelector('[data-artwork-confirm]').checked = false;
    this.querySelector('[data-artwork-name]').value = file?.name || '';
    const filename = this.querySelector('[data-artwork-filename]');
    filename.hidden = !file;
    filename.querySelector('span').textContent = file?.name || '';
    this.querySelector('[data-artwork-status]').textContent = '';
    input.setCustomValidity('');
    if (this.#url) URL.revokeObjectURL(this.#url);
    this.#url = undefined;
    if (!file) return;
    if (file.size > 10 * 1024 * 1024 || !/\.(png|jpe?g|webp|svg|pdf|eps|tiff?)$/i.test(file.name)) {
      const error = file.size > 10 * 1024 * 1024 ? this.dataset.fileLarge : this.dataset.fileType;
      input.setCustomValidity(error);
      this.querySelector('[data-artwork-status]').textContent = error;
      return;
    }
    if (!/\.(png|jpe?g|webp|svg)$/i.test(file.name)) {
      this.querySelector('[data-artwork-status]').textContent = this.dataset.noPreview;
      return;
    }
    this.#url = URL.createObjectURL(file);
    image.src = this.#url;
    try {
      await image.decode();
      if (serial !== this.#serial) return;
      image.hidden = false;
      this.querySelector('[data-artwork-placeholder]').hidden = true;
      this.applyLayout();
      this.checkResolution();
    } catch {
      if (serial === this.#serial) this.querySelector('[data-artwork-status]').textContent = this.dataset.previewFailed;
    }
  }

  updateShape(size) {
    this.querySelector('[data-artwork-size]').textContent = size;
    const numbers = size.match(/[0-9]+(?:\.[0-9]+)?/g)?.map(Number) || [1];
    this.dataset.widthMm = String(numbers[0]);
    this.dataset.heightMm = String(numbers[1] || numbers[0]);
    this.querySelector('[data-artwork-shape]').style.aspectRatio = this.dataset.shape === 'rectangle'
      ? String(numbers[0] / (numbers[1] || numbers[0])) : '1';
    this.checkResolution();
  }

  applyLayout() {
    this.querySelector('[data-artwork-image]').style.objectFit = this.querySelector('[data-artwork-layout]').selectedIndex === 0 ? 'contain' : 'cover';
    this.checkResolution();
  }

  checkResolution() {
    const image = this.querySelector('[data-artwork-image]');
    const file = this.querySelector('[data-artwork-file]').files?.[0];
    if (image.hidden || !file || /\.svg$/i.test(file.name)) return;
    const dpi = Math.min(image.naturalWidth / (Number(this.dataset.widthMm) / 25.4), image.naturalHeight / (Number(this.dataset.heightMm) / 25.4));
    this.querySelector('[data-artwork-status]').textContent = dpi < 300 ? this.dataset.lowResolution : '';
  }
}
if (!customElements.get('vig-sticker-artwork')) customElements.define('vig-sticker-artwork', StickerArtwork);
